import { useQuery } from '@tanstack/react-query'
import { CalendarSearch, Clock, DoorOpen, Pencil, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { errorMessage } from '@/api/errors'
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/States'
import { toast } from '@/components/feedback/toast-store'
import { Badge } from '@/components/ui/Badge'
import { Button, IconButton } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Dialog } from '@/components/ui/Dialog'
import { PageHeader } from '@/components/ui/PageHeader'
import { Can } from '@/features/auth/guards'
import {
  periodsQuery,
  roomTimetableQuery,
  roomsQuery,
  useDeletePeriod,
  useDeleteRoom,
} from '@/features/timetable/api'
import { PeriodDialog } from '@/features/timetable/components/PeriodDialog'
import { RoomDialog } from '@/features/timetable/components/RoomDialog'
import { WeekGrid } from '@/features/timetable/components/WeekGrid'
import type { Period, Room } from '@/features/timetable/types'
import { timeRange } from '@/features/timetable/week'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { humanize } from '@/utils/format'

function PeriodsCard() {
  const { t } = useTranslation('timetable')
  const { data, isPending, error, refetch } = useQuery(periodsQuery())
  const remove = useDeletePeriod()
  const [editing, setEditing] = useState<Period | 'new' | null>(null)
  const [deleting, setDeleting] = useState<Period | null>(null)

  const onDelete = async (p: Period) => {
    try {
      await remove.mutateAsync(p.id)
      toast.success(t('Period deleted'), p.name)
    } catch (e) {
      toast.error(t('Could not delete {{name}}', { name: p.name }), errorMessage(e))
    } finally {
      setDeleting(null)
    }
  }

  return (
    <Card>
      <CardHeader
        title={t('Periods')}
        description={t('The daily bell schedule. Periods of the same shift cannot overlap.')}
        icon={<Clock />}
        actions={
          <Can permission="academic.manage">
            <Button size="sm" variant="soft" leftIcon={<Plus />} onClick={() => setEditing('new')}>
              {t('Add period')}
            </Button>
          </Can>
        }
      />
      {isPending ? (
        <LoadingState className="min-h-32" />
      ) : error ? (
        <ErrorState compact onRetry={() => void refetch()} />
      ) : data.length === 0 ? (
        <EmptyState
          compact
          icon={<Clock />}
          title={t('No periods yet')}
          description={t('Add the first period of the day.')}
        />
      ) : (
        <ul className="divide-y divide-line">
          {data.map((p) => (
            <li
              key={p.id}
              className={
                p.isBreak
                  ? 'flex items-center gap-3 bg-surface-muted px-5 py-2.5'
                  : 'flex items-center gap-3 px-5 py-3'
              }
            >
              <span className="w-28 shrink-0 text-xs text-muted tabular">{timeRange(p)}</span>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium text-ink">{p.name}</span>
                <span className="flex flex-wrap gap-1 pt-0.5">
                  {p.isBreak ? (
                    <Badge size="sm">{t('Break')}</Badge>
                  ) : (
                    <span className="text-xs text-muted">
                      {t('{{count}} lessons', { count: p.lessonCount ?? 0 })}
                    </span>
                  )}
                  {p.shift ? (
                    <Badge size="sm" tone="info">
                      {p.shift.name}
                    </Badge>
                  ) : null}
                </span>
              </span>
              <Can permission="academic.manage">
                <IconButton
                  size="icon-sm"
                  label={t('Edit {{name}}', { name: p.name })}
                  icon={<Pencil />}
                  onClick={() => setEditing(p)}
                />
                <IconButton
                  size="icon-sm"
                  label={t('Delete {{name}}', { name: p.name })}
                  icon={<Trash2 />}
                  onClick={() => setDeleting(p)}
                />
              </Can>
            </li>
          ))}
        </ul>
      )}
      {editing ? (
        <PeriodDialog
          period={editing === 'new' ? undefined : editing}
          onOpenChange={(o) => (o ? undefined : setEditing(null))}
        />
      ) : null}
      {deleting ? (
        <ConfirmDialog
          open
          onOpenChange={(o) => (o ? undefined : setDeleting(null))}
          title={t('Delete “{{name}}”?', { name: deleting.name })}
          description={t('Periods that already hold lessons cannot be deleted.')}
          confirmLabel={t('Delete')}
          loading={remove.isPending}
          onConfirm={() => void onDelete(deleting)}
        />
      ) : null}
    </Card>
  )
}

function RoomWeekDialog({ room, onOpenChange }: { room: Room; onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation('timetable')
  const { data, isPending, error, refetch } = useQuery(roomTimetableQuery(room.id))
  return (
    <Dialog
      open
      onOpenChange={onOpenChange}
      size="xl"
      icon={<CalendarSearch />}
      title={t('{{room}} occupancy', { room: room.name })}
      description={data ? t('{{count}} periods booked this week', { count: data.totalPeriods }) : undefined}
    >
      {isPending ? (
        <LoadingState />
      ) : error ? (
        <ErrorState compact description={errorMessage(error)} onRetry={() => void refetch()} />
      ) : (
        <WeekGrid week={data} view="room" caption={t('{{room}} occupancy', { room: room.name })} />
      )}
    </Dialog>
  )
}

function RoomsCard() {
  const { t } = useTranslation('timetable')
  const { data, isPending, error, refetch } = useQuery(roomsQuery())
  const remove = useDeleteRoom()
  const [editing, setEditing] = useState<Room | 'new' | null>(null)
  const [deleting, setDeleting] = useState<Room | null>(null)
  const [viewing, setViewing] = useState<Room | null>(null)

  const onDelete = async (r: Room) => {
    try {
      await remove.mutateAsync(r.id)
      toast.success(t('Room deleted'), r.name)
    } catch (e) {
      toast.error(t('Could not delete {{name}}', { name: r.name }), errorMessage(e))
    } finally {
      setDeleting(null)
    }
  }

  return (
    <Card>
      <CardHeader
        title={t('Rooms')}
        description={t('Classrooms, labs and other spaces lessons can be booked in.')}
        icon={<DoorOpen />}
        actions={
          <Can permission="academic.manage">
            <Button size="sm" variant="soft" leftIcon={<Plus />} onClick={() => setEditing('new')}>
              {t('Add room')}
            </Button>
          </Can>
        }
      />
      {isPending ? (
        <LoadingState className="min-h-32" />
      ) : error ? (
        <ErrorState compact onRetry={() => void refetch()} />
      ) : data.length === 0 ? (
        <EmptyState
          compact
          icon={<DoorOpen />}
          title={t('No rooms yet')}
          description={t('Add the classrooms and labs of the school.')}
        />
      ) : (
        <ul className="divide-y divide-line">
          {data.map((r) => (
            <li key={r.id} className="flex items-center gap-3 px-5 py-3">
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2">
                  <span className="truncate font-medium text-ink">{r.name}</span>
                  {!r.active ? <Badge size="sm">{t('Inactive')}</Badge> : null}
                </span>
                <span className="block text-xs text-muted">
                  {humanize(r.kind)}
                  {r.capacity ? ` · ${t('{{count}} seats', { count: r.capacity })}` : ''}
                  {` · ${t('{{count}} lessons', { count: r.lessonCount ?? 0 })}`}
                </span>
              </span>
              <IconButton
                size="icon-sm"
                label={t('View occupancy of {{name}}', { name: r.name })}
                icon={<CalendarSearch />}
                onClick={() => setViewing(r)}
              />
              <Can permission="academic.manage">
                <IconButton
                  size="icon-sm"
                  label={t('Edit {{name}}', { name: r.name })}
                  icon={<Pencil />}
                  onClick={() => setEditing(r)}
                />
                <IconButton
                  size="icon-sm"
                  label={t('Delete {{name}}', { name: r.name })}
                  icon={<Trash2 />}
                  onClick={() => setDeleting(r)}
                />
              </Can>
            </li>
          ))}
        </ul>
      )}
      {editing ? (
        <RoomDialog
          room={editing === 'new' ? undefined : editing}
          onOpenChange={(o) => (o ? undefined : setEditing(null))}
        />
      ) : null}
      {viewing ? (
        <RoomWeekDialog room={viewing} onOpenChange={(o) => (o ? undefined : setViewing(null))} />
      ) : null}
      {deleting ? (
        <ConfirmDialog
          open
          onOpenChange={(o) => (o ? undefined : setDeleting(null))}
          title={t('Delete “{{name}}”?', { name: deleting.name })}
          description={t('Rooms that hold lessons cannot be deleted — deactivate them instead.')}
          confirmLabel={t('Delete')}
          loading={remove.isPending}
          onConfirm={() => void onDelete(deleting)}
        />
      ) : null}
    </Card>
  )
}

export function TimetableSetupPage() {
  const { t } = useTranslation('timetable')
  useDocumentTitle('Periods & rooms')
  return (
    <>
      <PageHeader
        title={t('Periods & rooms')}
        description={t('The bell schedule and the rooms used by class timetables.')}
        breadcrumbs={[{ label: t('Timetable'), to: '/admin/timetable' }, { label: t('Periods & rooms') }]}
        homeHref="/admin"
      />
      <div className="grid items-start gap-6 lg:grid-cols-2">
        <PeriodsCard />
        <RoomsCard />
      </div>
    </>
  )
}
